/*
 * bc-stock-indicator
 *
 * Reactive stock indicator (dot + label + optional ship date) that lives
 * alongside the Add-to-cart button. On `variant:update` (and direct option
 * `change` within scope) it swaps its display based on a per-variant state
 * map emitted by snippets/bc-stock-indicator.liquid — no server round-trip,
 * no morph dependency.
 */
(() => {
  if (customElements.get('bc-stock-indicator')) return;

  const STATE_CLASSES = ['in_stock', 'low', 'preorder', 'out'];
  const LABELS = {
    in_stock: 'In stock',
    preorder: 'Pre-Order',
    out: 'Out of stock',
  };

  class BcStockIndicator extends HTMLElement {
    constructor() {
      super();
      this._onVariantUpdate = this._onVariantUpdate.bind(this);
      this._onScopeChange = this._onScopeChange.bind(this);
    }

    connectedCallback() {
      try {
        this.states = JSON.parse(
          this.querySelector('script[data-bc-stock-states]')?.textContent || '{}'
        );
      } catch (_e) {
        this.states = {};
      }

      this.dotEl = this.querySelector('[data-key="dot"]');
      this.labelEl = this.querySelector('[data-key="label"]');
      this.dateEl = this.querySelector('[data-key="date"]');

      this.scope =
        this.closest('product-form-component') ||
        this.closest('.shopify-section') ||
        document;

      this._currentVariantId =
        this.scope.querySelector('input[name="id"]')?.value || null;

      this.scope.addEventListener('change', this._onScopeChange);
      document.addEventListener('variant:update', this._onVariantUpdate);
    }

    disconnectedCallback() {
      this.scope?.removeEventListener('change', this._onScopeChange);
      document.removeEventListener('variant:update', this._onVariantUpdate);
    }

    _onVariantUpdate(event) {
      // Scope to this product — variant:update bubbles across the page.
      const eventProductId = event?.detail?.data?.productId;
      if (eventProductId && eventProductId !== this.dataset.productId) return;

      const id = event?.detail?.resource?.id;
      if (id) {
        this._currentVariantId = String(id);
        this._render();
      }
    }

    _onScopeChange(event) {
      const t = event.target;
      if (!t || !t.matches) return;
      if (!t.matches('input[type="radio"]') && !t.matches('select')) return;
      // Wait a tick for the form's hidden variant id to settle.
      queueMicrotask(() => {
        const id = this.scope.querySelector('input[name="id"]')?.value;
        if (id && id !== this._currentVariantId) {
          this._currentVariantId = id;
          this._render();
        }
      });
    }

    _render() {
      const data = this.states[this._currentVariantId];
      if (!data || !data.state) {
        this.classList.remove(...STATE_CLASSES.map((s) => `bc-stock-indicator--${s}`));
        this.classList.add('bc-stock-indicator--hidden');
        if (this.labelEl) this.labelEl.textContent = '';
        if (this.dateEl) this.dateEl.textContent = '';
        return;
      }

      this.classList.remove('bc-stock-indicator--hidden');
      STATE_CLASSES.forEach((s) => this.classList.toggle(`bc-stock-indicator--${s}`, s === data.state));

      let label = LABELS[data.state] || '';
      if (data.state === 'low') label = `Low Stock - ${data.qty} left`;
      if (this.labelEl) this.labelEl.textContent = label;

      if (this.dateEl) {
        this.dateEl.textContent =
          data.state === 'preorder' && data.date ? ` — ships ${data.date}` : '';
      }
    }
  }

  customElements.define('bc-stock-indicator', BcStockIndicator);
})();
